
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';
/**
 * 订单物流选择 Panel 头部摘要溢出布局 Mixin
 *
 * 用于「订单物流选择」步骤中 Collapse Panel 头部的自适应布局：
 * 宽度不足时按 hidePriority 将低优先级字段收入 Tooltip，避免与右侧操作按钮重叠。
 *
 * 依赖组件提供：
 * - data: progressStatus、orderLogisticsData、orderLogisticsCollapseList
 * - methods: formatCurrency（来自 common_mixin）
 * - computed/data: currencyState
 * - template refs: orderLogisticsHeader_{ymsWarehouseId}、orderLogisticsActions_{ymsWarehouseId}
 */
export default {
  data() {
    return {
      // 各订单摘要溢出状态：{ [ymsWarehouseId]: { visibleKeys, hiddenKeys } }
      orderLogisticsSummaryOverflow: {},
      // 溢出重算防抖定时器
      orderLogisticsSummaryOverflowTimer: null
    };
  },
  mounted() {
    this.handleOrderLogisticsSummaryResize = () => {
      this.scheduleOrderLogisticsSummaryOverflowUpdate();
    };
    window.addEventListener('resize', this.handleOrderLogisticsSummaryResize);
    this.$nextTick(() => {
      this.scheduleOrderLogisticsSummaryOverflowUpdate();
    });
  },
  destroyed() {
    this.destroyOrderLogisticsSummaryOverflowListener();
  },
  watch: {
    orderLogisticsData: {
      handler() {
        this.scheduleOrderLogisticsSummaryOverflowUpdate();
      },
      deep: true,
      immediate: true
    },
    progressStatus(val) {
      if (val === 1) {
        this.scheduleOrderLogisticsSummaryOverflowUpdate();
      }
    },
    orderLogisticsCollapseList() {
      this.scheduleOrderLogisticsSummaryOverflowUpdate();
    }
  },
  methods: {
    /**
     * 构建订单物流摘要字段列表（按展示顺序排列）
     * hidePriority 越小越优先被收入 Tooltip（1=物流渠道名称，5=订单总金额）
     * @param {Object} item 当前订单物流数据
     * @return {Array<{key: string, text: string, hidePriority: number, style?: object, className?: string}>}
     */
    getOrderLogisticsSummaryFields(item) {
      const fields = [
        {
          key: 'totalOrderAmount',
          text: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000118') + this.formatCurrency(item.totalOrderAmount) + ' ' + this.currencyState,
          hidePriority: 5
        },
        {
          key: 'totalAmountOfGoods',
          text: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000119') + this.formatCurrency(item.totalAmountOfGoods) + ' ' + this.currencyState,
          hidePriority: 4
        },
        {
          key: 'processingFee',
          text: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000120') + this.formatCurrency(item.processingFee) + ' ' + this.currencyState,
          hidePriority: 3
        }
      ];
      if (item.carrierShippingMethodCode) {
        fields.push({
          key: 'shippingFee',
          text: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000121') + this.formatCurrency(item.shippingFee) + ' ' + this.currencyState,
          hidePriority: 2
        });
        fields.push({
          key: 'carrierName',
          text: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000122') + item.carrierShippingMethodName,
          hidePriority: 1
        });
      } else {
        fields.push({
          key: 'freightPending',
          text: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000123'),
          hidePriority: 2,
          style: {color: '#EA0000'}
        });
      }
      return fields;
    },
    /**
     * 获取当前 Panel 头部可直接展示的摘要字段
     * @param {Object} item 当前订单物流数据
     * @param {number} index 订单序号（从 0 开始）
     * @return {Array}
     */
    getOrderLogisticsVisibleFields(item, index) {
      const fields = this.getOrderLogisticsSummaryFields(item, index);
      const state = this.orderLogisticsSummaryOverflow[item.ymsWarehouseId];
      if (!state || !state.visibleKeys) {
        return fields;
      }
      return fields.filter(field => state.visibleKeys.includes(field.key));
    },
    /**
     * 获取当前 Panel 头部收入 Tooltip 的溢出字段
     * @param {Object} item 当前订单物流数据
     * @param {number} index 订单序号（从 0 开始）
     * @return {Array}
     */
    getOrderLogisticsHiddenFields(item, index) {
      const fields = this.getOrderLogisticsSummaryFields(item, index);
      const state = this.orderLogisticsSummaryOverflow[item.ymsWarehouseId];
      if (!state || !state.hiddenKeys || !state.hiddenKeys.length) {
        return [];
      }
      return fields.filter(field => state.hiddenKeys.includes(field.key));
    },
    /**
     * 测量摘要文本渲染宽度，用于溢出计算
     * @param {string} text 待测量文本
     * @return {number} 文本像素宽度（向上取整）
     */
    measureOrderLogisticsTextWidth(text) {
      if (!this._orderLogisticsMeasureCtx) {
        const canvas = document.createElement('canvas');
        this._orderLogisticsMeasureCtx = canvas.getContext('2d');
      }
      this._orderLogisticsMeasureCtx.font = '14px PingFangSC-Regular, Microsoft YaHei, sans-serif';
      return Math.ceil(this._orderLogisticsMeasureCtx.measureText(text).width);
    },
    /**
     * 防抖触发订单物流摘要溢出重算
     * 适用于 resize、数据变更、步骤切换、Panel 展开收起等场景
     */
    scheduleOrderLogisticsSummaryOverflowUpdate() {
      if (this.orderLogisticsSummaryOverflowTimer) {
        clearTimeout(this.orderLogisticsSummaryOverflowTimer);
      }
      this.orderLogisticsSummaryOverflowTimer = setTimeout(() => {
        this.$nextTick(() => {
          this.updateOrderLogisticsSummaryOverflow();
        });
      }, 100);
    },
    /**
     * 根据 Panel 头部可用宽度，计算各订单摘要的可见/隐藏字段
     * 宽度不足时按 hidePriority 从低到高将字段收入 Tooltip，并显示 ios-more 图标
     */
    updateOrderLogisticsSummaryOverflow() {
      if (this.progressStatus !== 1 || !this.orderLogisticsData.length) {
        this.orderLogisticsSummaryOverflow = {};
        return;
      }
      const FIELD_MARGIN = 20;
      const MORE_ICON_WIDTH = 26;
      const overflow = {};

      this.orderLogisticsData.forEach((item, index) => {
        const fields = this.getOrderLogisticsSummaryFields(item, index);
        const headerRef = this.$refs['orderLogisticsHeader_' + item.ymsWarehouseId];
        const actionsRef = this.$refs['orderLogisticsActions_' + item.ymsWarehouseId];
        const headerEl = Array.isArray(headerRef) ? headerRef[0] : headerRef;
        const actionsEl = Array.isArray(actionsRef) ? actionsRef[0] : actionsRef;

        if (!headerEl || !actionsEl || !headerEl.clientWidth) {
          overflow[item.ymsWarehouseId] = {
            visibleKeys: fields.map(field => field.key),
            hiddenKeys: []
          };
          return;
        }

        const availableWidth = headerEl.clientWidth - actionsEl.offsetWidth;
        let visibleKeys = fields.map(field => field.key);
        const hiddenKeySet = [];

        const getContentWidth = (visibleFieldKeys, withMoreIcon) => {
          let width = this.measureOrderLogisticsTextWidth(aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000124') + (index + 1)) + FIELD_MARGIN;
          fields.forEach((field) => {
            if (visibleFieldKeys.includes(field.key)) {
              width += this.measureOrderLogisticsTextWidth(field.text) + FIELD_MARGIN;
            }
          });
          if (withMoreIcon) {
            width += MORE_ICON_WIDTH;
          }
          return width;
        };

        while (visibleKeys.length > 0) {
          const needMoreIcon = hiddenKeySet.length > 0;
          if (getContentWidth(visibleKeys, needMoreIcon) <= availableWidth) {
            break;
          }
          let hideKey = null;
          let minPriority = Infinity;
          fields.forEach((field) => {
            if (visibleKeys.includes(field.key) && field.hidePriority < minPriority) {
              minPriority = field.hidePriority;
              hideKey = field.key;
            }
          });
          if (!hideKey) {
            break;
          }
          visibleKeys = visibleKeys.filter(key => key !== hideKey);
          hiddenKeySet.push(hideKey);
        }

        overflow[item.ymsWarehouseId] = {
          visibleKeys,
          hiddenKeys: fields.filter(field => hiddenKeySet.includes(field.key)).map(field => field.key)
        };
      });

      this.orderLogisticsSummaryOverflow = overflow;
    },
    /**
     * 重置溢出状态并清理防抖定时器
     * 适用于弹窗关闭等组件实例常驻但需清空布局状态的场景
     */
    resetOrderLogisticsSummaryOverflow() {
      if (this.orderLogisticsSummaryOverflowTimer) {
        clearTimeout(this.orderLogisticsSummaryOverflowTimer);
        this.orderLogisticsSummaryOverflowTimer = null;
      }
      this.orderLogisticsSummaryOverflow = {};
    },
    /**
     * 销毁 resize 监听并重置溢出状态
     * 在 destroyed 钩子中自动调用；弹窗类组件也可在关闭时手动调用 reset 方法
     */
    destroyOrderLogisticsSummaryOverflowListener() {
      if (this.handleOrderLogisticsSummaryResize) {
        window.removeEventListener('resize', this.handleOrderLogisticsSummaryResize);
        this.handleOrderLogisticsSummaryResize = null;
      }
      this.resetOrderLogisticsSummaryOverflow();
    }
  }
};
